import React, { useState, useEffect, useCallback } from "react";
import { Accessibility, Plus, Minus, RotateCcw, X } from "lucide-react";

const DEFAULTS = {
  fontPct: 100,
  grayscale: false,
  contrast: false,
  invert: false,
  highlightLinks: false,
  readable: false,
  stopAnim: false,
};

const STORAGE_KEY = "amor_accessibility";

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULTS, ...JSON.parse(saved) } : DEFAULTS;
    } catch {
      return DEFAULTS;
    }
  });

  const apply = useCallback((s) => {
    const root = document.getElementById("acc-root");
    document.documentElement.style.fontSize = `${s.fontPct}%`;
    if (root) {
      root.classList.toggle("acc-grayscale", s.grayscale);
      root.classList.toggle("acc-contrast", s.contrast);
      root.classList.toggle("acc-invert", s.invert);
      root.classList.toggle("acc-links", s.highlightLinks);
      root.classList.toggle("acc-readable", s.readable);
      root.classList.toggle("acc-no-anim", s.stopAnim);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  }, []);

  useEffect(() => {
    apply(state);
  }, [state, apply]);

  const set = (k, v) => setState((p) => ({ ...p, [k]: v }));
  const toggle = (k) => setState((p) => ({ ...p, [k]: !p[k] }));
  const reset = () => setState(DEFAULTS);

  const Toggle = ({ active, onClick, label }) => (
    <button
      onClick={onClick}
      className={`w-full text-right px-4 py-3 text-sm border transition-colors rounded-md ${
        active
          ? "bg-neutral-900 text-white border-neutral-900"
          : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900"
      }`}
    >
      {label}
    </button>
  );

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="תפריט נגישות"
        title="נגישות"
        className="fixed bottom-4 left-4 z-[10000] h-12 w-12 rounded-full bg-blue-700 text-white flex items-center justify-center shadow-lg hover:bg-blue-800 transition-colors focus:outline-none focus:ring-4 focus:ring-blue-300"
      >
        <Accessibility className="h-6 w-6" />
      </button>

      {open && (
        <div
          dir="rtl"
          className="fixed bottom-20 left-4 z-[10000] w-[300px] max-h-[80vh] overflow-y-auto bg-white rounded-xl shadow-2xl border border-neutral-200"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50 rounded-t-xl">
            <div className="flex items-center gap-2">
              <Accessibility className="h-5 w-5 text-blue-700" />
              <span className="text-sm font-semibold text-neutral-900">תפריט נגישות</span>
            </div>
            <button onClick={() => setOpen(false)} aria-label="סגור" className="text-neutral-500 hover:text-black">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            {/* Font size */}
            <div className="border border-neutral-200 rounded-md p-3">
              <p className="text-xs text-neutral-500 mb-2">גודל טקסט ({state.fontPct}%)</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => set("fontPct", Math.max(90, state.fontPct - 10))}
                  className="flex-1 flex items-center justify-center py-2 border border-neutral-300 rounded-md hover:border-neutral-900"
                  aria-label="הקטן טקסט"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <button
                  onClick={() => set("fontPct", Math.min(150, state.fontPct + 10))}
                  className="flex-1 flex items-center justify-center py-2 border border-neutral-300 rounded-md hover:border-neutral-900"
                  aria-label="הגדל טקסט"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <Toggle active={state.contrast} onClick={() => toggle("contrast")} label="ניגודיות גבוהה" />
            <Toggle active={state.invert} onClick={() => toggle("invert")} label="ניגודיות הפוכה" />
            <Toggle active={state.grayscale} onClick={() => toggle("grayscale")} label="גווני אפור" />
            <Toggle active={state.highlightLinks} onClick={() => toggle("highlightLinks")} label="הדגשת קישורים" />
            <Toggle active={state.readable} onClick={() => toggle("readable")} label="גופן קריא" />
            <Toggle active={state.stopAnim} onClick={() => toggle("stopAnim")} label="עצירת אנימציות" />

            <button
              onClick={reset}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm bg-neutral-100 text-neutral-700 rounded-md hover:bg-neutral-200 transition-colors"
            >
              <RotateCcw className="h-4 w-4" /> איפוס הגדרות
            </button>

            <a
              href="/accessibility"
              className="block text-center text-xs text-blue-700 underline pt-1"
            >
              להצהרת הנגישות המלאה
            </a>
          </div>
        </div>
      )}
    </>
  );
}
